import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {
  ANALYTICS_MODULE,
  ActionOrigin,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SearchInput from 'search/inputs/SearchInput';
import {FilterKey, Operator} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisSelectorQuery} from './__generated/AnalysisSelectorQuery';

export const ANALYSIS_SELECTOR_QUERY = gql`
  query AnalysisSelectorQuery($analysisId: StrictString!) @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    analysis(id: $analysisId) {
      ... on AnalysisNode {
        id
        name
        permissions {
          writable
        }
        archiveRecord {
          purpose
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/** @package */
export const READ_ONLY_WARNING = 'You do not have permission to edit this analysis.';

function onlyWhiteSpace(str: string): boolean {
  return str.trim().length === 0;
}

export interface AddDatasetToAnalysisConfiguration {
  dataFrameName: string;
  analysisId: string;
}

export interface AddDatasetToAnalysisConfigurationModalProps {
  onCreateDataFrame: (params: AddDatasetToAnalysisConfiguration) => Promise<unknown>;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  /** Text to prefill to the dataset name field */
  initialDatasetName?: string;
}

export default function AddDatasetToAnalysisConfigurationModal(
  props: AddDatasetToAnalysisConfigurationModalProps
): JSX.Element {
  const dataFrameNameFormInput = useFormInput(props.initialDatasetName ?? '');
  const dataFrameName = dataFrameNameFormInput.value;

  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);

  const dataFrameNameValid = dataFrameName != null && !onlyWhiteSpace(dataFrameName);
  const analysisValid = selectedAnalysisId != null; // TODO

  const analyticsMetadata: AnalyticsMetadataForContext = {
    actionOrigin: ActionOrigin.NOTEBOOK,
    modalType: 'create_dataset',
  };
  const analyticsModule = ANALYTICS_MODULE.ANALYSIS_TOOL;

  async function handleRequestSubmit(): Promise<void> {
    assert(canSubmit, 'Unexpectedly creating analysis with null dataset name or analysis');
    await withLoadingScreen(
      props.onCreateDataFrame({dataFrameName, analysisId: selectedAnalysisId}),
      'Adding analysis table to analysis...'
    );
    props.onRequestSubmit();
  }

  useOnMount(() => {
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, analyticsModule, analyticsMetadata);
  });

  let selectedAnalysisIsReadOnly: boolean = false;

  const analysisSelectorQueryResult = useSkippableBenchlingQuery<AnalysisSelectorQuery>(
    ANALYSIS_SELECTOR_QUERY,
    {
      variables: {analysisId: selectedAnalysisId!},
      skip: selectedAnalysisId == null,
    }
  );
  if (selectedAnalysisId != null && analysisSelectorQueryResult.type === QueryResultType.SUCCESS) {
    const {analysis} = analysisSelectorQueryResult.data;
    assert(!isInaccessible(analysis), 'User does not have access to this analysis.');
    if (!analysis.permissions.writable) {
      selectedAnalysisIsReadOnly = true;
    }
  }

  const canSubmit = dataFrameNameValid && analysisValid && !selectedAnalysisIsReadOnly;

  return (
    <Modal
      title="Add to existing analysis"
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        handleRequestSubmit();
      }}
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        props.onRequestClose();
      }}
      buttons={[
        {
          submit: true,
          text: 'Create',
          style: ButtonStyle.SECONDARY,
          disabled: !canSubmit,
        },
      ]}
    >
      <FormGroup>
        <FieldLabel label="Analysis" />
        <SearchInput
          autoFocus={false}
          disableTyping={selectedAnalysisId != null}
          isMulti={false}
          onRemove={() => {
            setSelectedAnalysisId(null);
          }}
          onSelect={setSelectedAnalysisId}
          placeholder="Search for analysis..."
          selected={selectedAnalysisId == null ? [] : [selectedAnalysisId]}
          types={[ModelTypeConstants.ANALYSIS]}
          additionalFilters={[
            {
              key: FilterKey.ARCHIVE_PURPOSES,
              operator: Operator.IS,
              value: [NOT_ARCHIVED_PURPOSE_STRING],
            },
          ]}
        />
        <div className={css(styles.bannerSection)}>
          {selectedAnalysisIsReadOnly && (
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text={READ_ONLY_WARNING}
              dataTestAttrs={{
                component: 'AnalysisSelector',
                element: 'readOnlyBanner',
              }}
            />
          )}
        </div>
      </FormGroup>
      <FormGroup>
        <TextField
          label="Analysis table name"
          autoFocus={false}
          dataTestAttrs={{component: 'CreateAnalysisModal', element: 'dataFrameTextField'}}
          {...dataFrameNameFormInput}
        />
      </FormGroup>
    </Modal>
  );
}

const styles = StyleSheet.create({
  bannerSection: {
    marginTop: 4,
  },
});
